import { Dialog } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Formik, useFormikContext } from "formik";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import { ProductFormData, SkuFormData } from "@/components/screens/ProductFormPage/types";
import { normalizeSkuEditingFormData } from "@/components/templates/ProductForm/form-data";
import useToast from "@/contexts/toast-context";
import useLoading from "@/hooks/use-loading";
import { ISku } from "@/interfaces/sku.interface";
import { ProductFormPageMessages } from "@/messages/products/product-form-page.messages";
import { useProductFormStateContext } from "@/contexts/product-form-state-context";

import KitComponents from "./components/KitComponents";
import Pricing from "./components/Pricing";
import SkuInformation from "./components/SkuInformation";
import Storage from "./components/Storage";
import VariationImageSelector from "./components/VariationImageSelector";
import Variations from "./components/Variations";
import {
  buildSkuDataFormSchema,
  buildSkuFormPageInitialData,
  checkPromoPriceConditions,
  checkVariations,
} from "../../form-data";
import {
  DIALOG_MARGIN_TOP,
  DIALOG_MAX_HEIGHT,
  DIALOG_MAX_WIDTH,
  DIALOG_WIDTH,
  FONT_SIZES,
  FORM_PADDING,
  GRID_SIZES,
  GRID_SPACING,
  SECTION_MARGIN_TOP,
} from "./constants";

interface Props {
  isVisible: boolean;
  handleClose: () => void;
  messages: ProductFormPageMessages;
  sku: ISku | null;
  isEditingSku: boolean;
}

const SkuFormDialog = ({ isVisible, handleClose, messages, sku, isEditingSku }: Props) => {
  const { isLoading, startLoading, endLoading } = useLoading();
  const { showToast } = useToast();
  const { values, setFieldValue: setFieldValueProduct } = useFormikContext<ProductFormData>();
  const { variationCode } = values;
  const { savedImages } = useProductFormStateContext();

  const isVariationProduct = !!variationCode;

  const handleCreate = (_formData: SkuFormData) => {
    // Adicionar SKU à lista local
    const updatedSkuList = [...values.skus, _formData];
    setFieldValueProduct("skus", updatedSkuList);
    handleClose();
  };

  const handleUpdate = async (_formData: SkuFormData) => {
    if (!sku) return;

    startLoading();
    const formData = normalizeSkuEditingFormData(_formData);

    const updatedSkus = values.skus.map((existingSku) =>
      existingSku.code === sku.code ? formData : existingSku
    );
    setFieldValueProduct("skus", updatedSkus);
    endLoading();
    handleClose();
  };

  async function handleSubmit(formData: SkuFormData) {
    if (await checkPromoPriceConditions(formData, messages, showToast)) return;

    const formVariations = formData.variations as string;
    const hasVariations = !!variationCode;

    if (hasVariations) {
      if (await checkVariations(formVariations, messages, showToast)) return;
    }

    if (values.isEditingSku) {
      await handleUpdate(formData);
    } else {
      handleCreate(formData);
    }
  }

  return (
    <Dialog
      open={isVisible}
      onClose={handleClose}
      style={{ marginTop: DIALOG_MARGIN_TOP }}
      fullWidth
      maxWidth={false}
      PaperProps={{
        style: {
          maxWidth: DIALOG_MAX_WIDTH,
          width: DIALOG_WIDTH,
          maxHeight: DIALOG_MAX_HEIGHT,
        },
      }}
    >
      <Formik
        key={sku?.id ?? sku?.code ?? "new"}
        enableReinitialize
        initialValues={buildSkuFormPageInitialData(sku, values, savedImages)}
        validationSchema={buildSkuDataFormSchema(messages, isVariationProduct)}
        validateOnChange={false}
        onSubmit={handleSubmit}
      >
        {(formik) => {
          return (
            <MDBox
              component="form"
              p={FORM_PADDING}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: FONT_SIZES.INPUT,
                },
                "& .MuiInputLabel-root": {
                  fontSize: FONT_SIZES.LABEL,
                },
                "& .MuiTypography-h5": {
                  fontSize: `${FONT_SIZES.HEADING} !important`,
                },
                "& .MuiTypography-body1": {
                  fontSize: FONT_SIZES.BODY,
                },
              }}
            >
              <Grid container spacing={GRID_SPACING}>
                <Grid size={GRID_SIZES.LEFT_PANEL}>
                  <SkuInformation messages={messages} isVariationProduct={isVariationProduct} />

                  <MDBox mt={SECTION_MARGIN_TOP}>
                    <Grid container spacing={GRID_SPACING}>
                      <Grid size={GRID_SIZES.STORAGE}>
                        <Storage messages={messages} isEditingSku={isEditingSku} />
                      </Grid>
                      <Grid size={GRID_SIZES.PRICING}>
                        <Pricing messages={messages} />
                      </Grid>
                    </Grid>
                  </MDBox>

                  {isVariationProduct && (
                    <MDBox mt={SECTION_MARGIN_TOP}>
                      <Variations
                        messages={messages}
                        product={values}
                        isEditingSku={isEditingSku}
                      />
                    </MDBox>
                  )}

                  <MDBox mt={SECTION_MARGIN_TOP}>
                    <KitComponents messages={messages} isEditingSku={isEditingSku} />
                  </MDBox>
                </Grid>

                <Grid size={GRID_SIZES.RIGHT_PANEL}>
                  <VariationImageSelector messages={messages} />
                </Grid>
              </Grid>

              <MDBox mt={2} display="flex" justifyContent="flex-end" gap={2}>
                <MDButton
                  variant="gradient"
                  color="light"
                  onClick={handleClose}
                  isLoading={isLoading}
                >
                  {messages.cancelButton}
                </MDButton>

                <MDButton
                  variant="gradient"
                  color="dark"
                  onClick={formik.submitForm}
                  isLoading={isLoading}
                >
                  {messages.updateButton}
                </MDButton>
              </MDBox>
            </MDBox>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default SkuFormDialog;
